import { VehicleBodyType } from './vehicle-damage-parts';
import suvShapes from '../../../../assets/vehicle-diagrams/suv/shapes.json';
import sedanShapes from '../../../../assets/vehicle-diagrams/sedan/shapes.json';
import hatchShapes from '../../../../assets/vehicle-diagrams/hatchback/shapes.json';

export type DamageView = 'front' | 'rear' | 'left' | 'right';
export type ShapeTone = 'shadow' | 'paint' | 'glass' | 'tire' | 'rim' | 'hub' | 'light' | 'tail' | 'trim' | 'grille' | 'chrome' | 'seam' | 'hit';

export interface DiagramShape {
  id: string;
  kind: 'path' | 'circle' | 'ellipse';
  tone: ShapeTone;
  d?: string;
  cx?: number;
  cy?: number;
  r?: number;
  rx?: number;
  ry?: number;
}

export const SIDE_SWAP: Record<string, string> = {
  wheel_fl: 'wheel_fr', wheel_fr: 'wheel_fl',
  wheel_rl: 'wheel_rr', wheel_rr: 'wheel_rl',
  door_fl: 'door_fr', door_fr: 'door_fl',
  door_rl: 'door_rr', door_rr: 'door_rl',
  fender_fl: 'fender_fr', fender_fr: 'fender_fl',
  fender_rl: 'fender_rr', fender_rr: 'fender_rl',
  mirror_left: 'mirror_right', mirror_right: 'mirror_left',
  headlight_left: 'headlight_right', headlight_right: 'headlight_left',
  taillight_left: 'taillight_right', taillight_right: 'taillight_left',
  glass_fl: 'glass_fr', glass_fr: 'glass_fl',
  glass_rl: 'glass_rr', glass_rr: 'glass_rl',
  glass_ql: 'glass_qr', glass_qr: 'glass_ql'
};

/** Frente: el auto mira a cámara, así que izquierda visual = derecha del vehículo. Derecha: el plano lateral se refleja. */
export function partIdForView(view: DamageView, id: string): string {
  if (!id) return id;
  if (view === 'right' || view === 'front') return SIDE_SWAP[id] ?? id;
  return id;
}

export function stackHits<T extends { id: string }>(shapes: T[]): T[] {
  return [...shapes].sort((a, b) => _hitRank(a.id) - _hitRank(b.id));
}

function _hitRank(id: string): number {
  if (!id) return 0;
  if (id.startsWith('wheel_')) return 50;
  if (id.includes('headlight') || id.includes('taillight')) return 40;
  if (id.includes('mirror')) return 30;
  if (id.startsWith('glass_')) return 20;
  return 10;
}

export interface DiagramAsset {
  url: string;
  viewBox: string;
  width: number;
}

type AssetView = 'front' | 'rear' | 'side';

const ASSETS: Partial<Record<VehicleBodyType, Partial<Record<AssetView, DiagramAsset>>>> = {
  sedan: {
    side: { url: 'assets/vehicle-diagrams/sedan/side.svg', viewBox: '0 0 1774 887', width: 1774 },
    front: { url: 'assets/vehicle-diagrams/sedan/front.svg', viewBox: '0 0 1254 1254', width: 1254 },
    rear: { url: 'assets/vehicle-diagrams/sedan/rear.svg', viewBox: '0 0 1254 1254', width: 1254 }
  },
  hatchback: {
    side: { url: 'assets/vehicle-diagrams/hatchback/side.svg', viewBox: '0 0 2646 1086', width: 2646 },
    front: { url: 'assets/vehicle-diagrams/hatchback/front.svg', viewBox: '0 0 1765 1316', width: 1765 },
    rear: { url: 'assets/vehicle-diagrams/hatchback/rear.svg', viewBox: '0 0 1697 1332', width: 1697 }
  },
  suv: {
    side: { url: 'assets/vehicle-diagrams/suv/side.svg', viewBox: '0 0 2749 1144', width: 2749 },
    front: { url: 'assets/vehicle-diagrams/suv/front.svg', viewBox: '0 0 1654 1337', width: 1654 },
    rear: { url: 'assets/vehicle-diagrams/suv/rear.svg', viewBox: '0 0 1333 1172', width: 1333 }
  }
};

function assetView(view: DamageView): AssetView {
  return view === 'front' || view === 'rear' ? view : 'side';
}

export function diagramAsset(type: VehicleBodyType, view: DamageView): DiagramAsset | null {
  return ASSETS[type]?.[assetView(view)] ?? null;
}

function _factoryHits(type: VehicleBodyType, view: DamageView): DiagramShape[] {
  const asset = diagramAsset(type, view);
  if (asset) return overlayHits(type, view);
  return view === 'front' ? _front(type) : view === 'rear' ? _rear(type) : _side(type);
}

function _mergeHits(factory: DiagramShape[], configured?: DiagramShape[] | null): DiagramShape[] {
  if (!configured?.length) return factory;
  const byId = new Map<string, DiagramShape>();
  for (const shape of factory) {
    if (shape.id) byId.set(shape.id, shape);
    else byId.set(`anon-${byId.size}`, shape);
  }
  for (const shape of configured) {
    if (shape?.id) byId.set(shape.id, shape);
  }
  return [...byId.values()];
}

export function shapesFor(type: VehicleBodyType, view: DamageView, configured?: DiagramShape[] | null): DiagramShape[] {
  return prepareShapes(_mergeHits(_factoryHits(type, view), configured), view);
}

export function prepareShapes(raw: DiagramShape[], view: DamageView): DiagramShape[] {
  const stacked = stackHits(_layer(raw));
  if (view !== 'right' && view !== 'front') return stacked;
  return stacked.map(shape => ({ ...shape, id: partIdForView(view, shape.id) }));
}

const LAYER: ShapeTone[] = ['shadow', 'tire', 'rim', 'hub', 'paint', 'seam', 'hit', 'trim', 'grille', 'glass', 'light', 'tail', 'chrome'];

function _layer(shapes: DiagramShape[]): DiagramShape[] {
  return [...shapes].sort((a, b) => LAYER.indexOf(a.tone) - LAYER.indexOf(b.tone));
}

export function mirrorView(view: DamageView): boolean {
  return view === 'right';
}

export function viewBoxFor(type: VehicleBodyType, view: DamageView): string {
  const asset = diagramAsset(type, view);
  if (asset) return asset.viewBox;
  if (view === 'left' || view === 'right') return '0 0 1040 420';
  return '0 0 640 520';
}

/** Límites propios de cada tipo. Sin fallback entre carrocerías. */
const HITS: Partial<Record<VehicleBodyType, Partial<Record<AssetView, () => DiagramShape[]>>>> = {
  sedan: {
    side: _sedanSideAssetHits,
    front: _sedanFrontAssetHits,
    rear: _sedanRearAssetHits
  },
  hatchback: {
    side: _hatchSideAssetHits,
    front: _hatchFrontAssetHits,
    rear: _hatchRearAssetHits
  },
  suv: {
    side: _suvSideAssetHits,
    front: _suvFrontAssetHits,
    rear: _suvRearAssetHits
  }
};

const JSON_SHAPES: Partial<Record<VehicleBodyType, Record<string, DiagramShape[]>>> = {
  suv: suvShapes as unknown as Record<string, DiagramShape[]>,
  sedan: sedanShapes as unknown as Record<string, DiagramShape[]>,
  hatchback: hatchShapes as unknown as Record<string, DiagramShape[]>
};

function overlayHits(type: VehicleBodyType, view: DamageView): DiagramShape[] {
  const fromJson = JSON_SHAPES[type]?.[assetView(view)];
  return fromJson?.length ? fromJson : (HITS[type]?.[assetView(view)]?.() ?? []);
}

function _sedanSideAssetHits(): DiagramShape[] {
  return [
    _circle('wheel_fl', 'hit', 344, 596, 145),
    _circle('wheel_rl', 'hit', 1376, 598, 145),
    _path('roof', 'hit', 'M 560 160 L 1210 155 L 1195 240 L 580 245 Z'),
    _path('glass_fl', 'hit', 'M 510 245 L 720 165 L 860 165 L 850 365 L 530 370 Z'),
    _path('glass_rl', 'hit', 'M 875 165 L 1200 165 L 1260 280 L 1230 365 L 870 365 Z'),
    _path('door_fl', 'hit', 'M 540 370 L 855 365 L 855 640 L 570 640 L 535 440 Z'),
    _path('door_rl', 'hit', 'M 865 365 L 1180 365 L 1185 530 Q 1240 540, 1260 640 L 865 640 Z'),
    _path('hood', 'hit', 'M 195 385 L 520 280 L 540 400 L 220 445 Z'),
    _path('fender_fl', 'hit', 'M 220 445 L 535 400 L 550 510 Q 420 500, 344 450 Q 240 500, 210 470 Z'),
    _path('fender_rl', 'hit', 'M 1180 365 L 1520 380 L 1540 520 Q 1480 480, 1376 460 Q 1280 500, 1185 530 Z'),
    _path('bumper_front', 'hit', 'M 65 470 L 210 445 L 240 580 L 340 680 L 120 670 Z'),
    _path('bumper_rear', 'hit', 'M 1540 435 L 1710 420 L 1700 630 L 1570 660 L 1520 560 Z'),
    _path('trunk', 'hit', 'M 1210 240 L 1530 290 L 1540 385 L 1230 365 Z'),
    _path('headlight_left', 'hit', 'M 110 395 L 220 380 L 230 435 L 120 450 Z'),
    _path('taillight_left', 'hit', 'M 1520 310 L 1650 325 L 1640 380 L 1510 365 Z'),
    _path('mirror_left', 'hit', 'M 525 310 C 585 285, 630 320, 600 355 C 560 370, 510 340, 525 310 Z')
  ];
}

function _hatchSideAssetHits(): DiagramShape[] {
  return [
    _circle('wheel_fl', 'hit', 450, 760, 165),
    _circle('wheel_rl', 'hit', 2050, 760, 165),
    _path('roof', 'hit', 'M 870 170 L 2160 160 L 2140 250 L 890 260 Z'),
    _path('glass_fl', 'hit', 'M 810 260 L 1150 170 L 1480 170 L 1470 420 L 830 430 Z'),
    _path('glass_rl', 'hit', 'M 1500 170 L 2150 170 L 2260 300 L 2080 420 L 1500 420 Z'),
    _path('door_fl', 'hit', 'M 850 430 L 1470 420 L 1470 820 L 890 820 L 850 540 Z'),
    _path('door_rl', 'hit', 'M 1480 420 L 1980 420 L 1990 640 Q 2020 660, 2040 820 L 1480 820 Z'),
    _path('hood', 'hit', 'M 240 480 L 820 370 L 850 510 L 260 550 Z'),
    _path('fender_fl', 'hit', 'M 260 550 L 850 510 L 870 630 Q 640 680, 450 630 Q 300 680, 250 590 Z'),
    _path('fender_rl', 'hit', 'M 1980 420 L 2350 430 L 2380 620 Q 2240 660, 2050 630 L 1990 640 Z'),
    _path('bumper_front', 'hit', 'M 60 620 L 250 550 L 270 700 L 450 860 L 150 860 Z'),
    _path('bumper_rear', 'hit', 'M 2380 580 L 2560 570 L 2540 820 L 2360 840 L 2280 730 Z'),
    _path('trunk', 'hit', 'M 2150 170 L 2440 330 L 2480 570 L 2280 570 L 2260 300 Z'),
    _path('headlight_left', 'hit', 'M 120 540 L 260 510 L 280 580 L 140 600 Z'),
    _path('taillight_left', 'hit', 'M 2340 370 L 2460 390 L 2450 470 L 2330 450 Z'),
    _path('mirror_left', 'hit', 'M 820 380 C 890 350, 950 390, 920 430 C 870 445, 800 415, 820 380 Z')
  ];
}

function _hatchFrontAssetHits(): DiagramShape[] {
  return [
    _ellipse('wheel_fl', 'hit', 210, 1160, 80, 110),
    _ellipse('wheel_fr', 'hit', 1555, 1160, 80, 110),
    _path('roof', 'hit', 'M 400 48 L 1365 48 L 1340 120 L 425 120 Z'),
    _path('glass_front', 'hit', 'M 425 120 L 1340 120 L 1420 480 L 345 480 Z'),
    _path('hood', 'hit', 'M 350 480 L 1415 480 L 1370 650 L 1180 660 L 585 660 L 395 650 Z'),
    _path('headlight_left', 'hit', 'M 200 600 L 395 650 L 380 750 L 190 680 Z'),
    _path('headlight_right', 'hit', 'M 1565 600 L 1370 650 L 1385 750 L 1575 680 Z'),
    _path('fender_fl', 'hit', 'M 170 540 L 350 480 L 395 650 L 200 780 L 180 880 Z'),
    _path('fender_fr', 'hit', 'M 1595 540 L 1415 480 L 1370 650 L 1565 780 L 1585 880 Z'),
    _path('bumper_front', 'hit', 'M 190 770 L 1575 770 L 1540 1140 L 225 1140 Z'),
    _path('mirror_left', 'hit', 'M 40 380 H 195 L 180 475 H 50 Z'),
    _path('mirror_right', 'hit', 'M 1725 380 H 1570 L 1585 475 H 1715 Z')
  ];
}

function _hatchRearAssetHits(): DiagramShape[] {
  return [
    _ellipse('wheel_rl', 'hit', 190, 1160, 80, 110),
    _ellipse('wheel_rr', 'hit', 1507, 1160, 80, 110),
    _path('roof', 'hit', 'M 360 28 L 1337 28 L 1310 95 L 387 95 Z'),
    _path('glass_rear', 'hit', 'M 387 95 L 1310 95 L 1350 400 L 347 400 Z'),
    _path('trunk', 'hit', 'M 347 400 L 1350 400 L 1370 780 L 327 780 Z'),
    _path('taillight_left', 'hit', 'M 185 385 H 290 V 660 H 185 Z'),
    _path('taillight_right', 'hit', 'M 1512 385 H 1407 V 660 H 1512 Z'),
    _path('fender_rl', 'hit', 'M 145 500 L 327 500 L 320 800 L 155 820 Z'),
    _path('fender_rr', 'hit', 'M 1552 500 L 1370 500 L 1377 800 L 1542 820 Z'),
    _path('bumper_rear', 'hit', 'M 165 800 L 1532 800 L 1490 1100 L 207 1100 Z'),
    _path('mirror_left', 'hit', 'M 20 345 H 175 L 160 455 H 30 Z'),
    _path('mirror_right', 'hit', 'M 1677 345 H 1522 L 1537 455 H 1667 Z')
  ];
}

function _suvSideAssetHits(): DiagramShape[] {
  return [
    _circle('wheel_fl', 'hit', 537, 925, 208),
    _circle('wheel_rl', 'hit', 2137, 925, 210),
    _path('roof', 'hit', 'M 930 70 L 2260 65 L 2250 140 L 960 145 Z'),
    _path('glass_fl', 'hit', 'M 870 415 L 1180 135 L 1485 135 L 1485 415 L 945 415 Z'),
    _path('glass_rl', 'hit', 'M 1530 135 L 2070 135 L 2069 391 L 1535 415 Z'),
    _path('glass_ql', 'hit', 'M 2070 140 L 2370 120 L 2480 200 L 2460 380 L 2080 395 Z'),
    _path('door_fl', 'hit', 'M 945 420 L 1485 420 L 1485 845 L 920 845 L 945 420 Z'),
    _path('door_rl', 'hit', 'M 1495 415 L 2050 415 L 2060 680 Q 2030 680, 1930 845 L 1495 845 Z'),
    _path('hood', 'hit', 'M 270 500 L 880 390 L 940 435 L 270 540 Z'),
    _path('fender_fl', 'hit', 'M 270 540 L 940 435 L 920 845 L 745 745 Q 537 680, 330 925 L 270 780 Z'),
    _path('fender_rl', 'hit', 'M 2050 415 L 2580 430 L 2610 680 L 2350 715 Q 2137 680, 1930 845 L 2060 680 Z'),
    _path('bumper_front', 'hit', 'M 65 785 L 145 560 L 270 540 L 270 780 L 330 925 L 110 930 Z'),
    _path('bumper_rear', 'hit', 'M 2580 600 L 2725 615 L 2715 840 L 2440 860 L 2350 715 L 2610 680 Z'),
    _path('trunk', 'hit', 'M 2260 65 L 2670 350 L 2595 390 L 2440 105 Z'),
    _path('headlight_left', 'hit', 'M 140 560 L 270 505 L 290 545 L 160 590 Z'),
    _path('taillight_left', 'hit', 'M 2595 390 L 2670 350 L 2685 440 L 2610 470 Z'),
    _path('mirror_left', 'hit', 'M 900 370 C 970 330, 1030 370, 990 415 C 930 435, 870 400, 900 370 Z')
  ];
}

function _suvFrontAssetHits(): DiagramShape[] {
  return [
    _ellipse('wheel_fl', 'hit', 215, 1120, 85, 130),
    _ellipse('wheel_fr', 'hit', 1439, 1120, 85, 130),
    _path('roof', 'hit', 'M 340 50 L 1314 50 L 1280 140 L 374 140 Z'),
    _path('glass_front', 'hit', 'M 374 140 L 1280 140 L 1370 510 L 284 510 Z'),
    _path('hood', 'hit', 'M 290 510 L 1364 510 L 1300 660 L 1150 670 L 504 670 L 354 660 Z'),
    _path('headlight_left', 'hit', 'M 150 560 L 354 660 L 340 760 L 140 680 Z'),
    _path('headlight_right', 'hit', 'M 1504 560 L 1300 660 L 1314 760 L 1514 680 Z'),
    _path('fender_fl', 'hit', 'M 120 540 L 290 510 L 354 660 L 150 780 L 130 920 Z'),
    _path('fender_fr', 'hit', 'M 1534 540 L 1364 510 L 1300 660 L 1504 780 L 1524 920 Z'),
    _path('bumper_front', 'hit', 'M 150 780 L 1504 780 L 1480 1160 L 174 1160 Z'),
    _path('mirror_left', 'hit', 'M 40 370 H 180 L 165 470 H 50 Z'),
    _path('mirror_right', 'hit', 'M 1614 370 H 1474 L 1489 470 H 1604 Z')
  ];
}

function _suvRearAssetHits(): DiagramShape[] {
  return [
    _ellipse('wheel_rl', 'hit', 165, 990, 70, 115),
    _ellipse('wheel_rr', 'hit', 1168, 990, 70, 115),
    _path('roof', 'hit', 'M 280 35 L 1053 35 L 1030 110 L 303 110 Z'),
    _path('glass_rear', 'hit', 'M 303 110 L 1030 110 L 1060 365 L 273 365 Z'),
    _path('trunk', 'hit', 'M 273 365 L 1060 365 L 1070 750 L 263 750 Z'),
    _path('taillight_left', 'hit', 'M 155 375 L 270 375 L 265 640 L 150 630 Z'),
    _path('taillight_right', 'hit', 'M 1178 375 L 1063 375 L 1068 640 L 1183 630 Z'),
    _path('fender_rl', 'hit', 'M 110 460 L 263 460 L 255 750 L 120 780 Z'),
    _path('fender_rr', 'hit', 'M 1223 460 L 1070 460 L 1078 750 L 1213 780 Z'),
    _path('bumper_rear', 'hit', 'M 120 750 L 1213 750 L 1180 1020 L 153 1020 Z'),
    _path('mirror_left', 'hit', 'M 15 325 H 140 L 125 425 H 25 Z'),
    _path('mirror_right', 'hit', 'M 1318 325 H 1193 L 1208 425 H 1308 Z')
  ];
}

function _sedanFrontAssetHits(): DiagramShape[] {
  return [
    _ellipse('wheel_fl', 'hit', 180, 1020, 60, 95),
    _ellipse('wheel_fr', 'hit', 1074, 1020, 60, 95),
    _path('roof', 'hit', 'M 330 178 L 924 178 L 900 238 L 354 238 Z'),
    _path('glass_front', 'hit', 'M 354 238 L 900 238 L 965 480 L 289 480 Z'),
    _path('hood', 'hit', 'M 290 480 L 964 480 L 920 620 L 800 630 L 454 630 L 334 620 Z'),
    _path('headlight_left', 'hit', 'M 175 540 L 334 620 L 315 700 L 160 640 Z'),
    _path('headlight_right', 'hit', 'M 1079 540 L 920 620 L 939 700 L 1094 640 Z'),
    _path('fender_fl', 'hit', 'M 150 510 L 290 480 L 334 620 L 160 740 L 150 850 Z'),
    _path('fender_fr', 'hit', 'M 1104 510 L 964 480 L 920 620 L 1094 740 L 1104 850 Z'),
    _path('bumper_front', 'hit', 'M 160 720 L 1094 720 L 1060 970 L 194 970 Z'),
    _path('mirror_left', 'hit', 'M 25 365 H 155 L 145 435 H 35 Z'),
    _path('mirror_right', 'hit', 'M 1229 365 H 1099 L 1109 435 H 1219 Z')
  ];
}

function _sedanRearAssetHits(): DiagramShape[] {
  return [
    _ellipse('wheel_rl', 'hit', 180, 1020, 60, 95),
    _ellipse('wheel_rr', 'hit', 1074, 1020, 60, 95),
    _path('roof', 'hit', 'M 330 172 L 924 172 L 900 232 L 354 232 Z'),
    _path('glass_rear', 'hit', 'M 354 232 L 900 232 L 945 410 L 309 410 Z'),
    _path('trunk', 'hit', 'M 309 410 L 945 410 L 955 640 L 299 640 Z'),
    _path('taillight_left', 'hit', 'M 165 500 L 299 500 L 295 620 L 160 610 Z'),
    _path('taillight_right', 'hit', 'M 1089 500 L 955 500 L 959 620 L 1094 610 Z'),
    _path('fender_rl', 'hit', 'M 130 500 L 299 500 L 290 680 L 140 700 Z'),
    _path('fender_rr', 'hit', 'M 1124 500 L 955 500 L 964 680 L 1114 700 Z'),
    _path('bumper_rear', 'hit', 'M 140 680 L 1114 680 L 1080 940 L 174 940 Z'),
    _path('mirror_left', 'hit', 'M 25 400 H 155 L 145 470 H 35 Z'),
    _path('mirror_right', 'hit', 'M 1229 400 H 1099 L 1109 470 H 1219 Z')
  ];
}

function _path(id: string, tone: ShapeTone, d: string): DiagramShape {
  return { id, kind: 'path', tone, d };
}

function _circle(id: string, tone: ShapeTone, cx: number, cy: number, r: number): DiagramShape {
  return { id, kind: 'circle', tone, cx, cy, r };
}

function _ellipse(id: string, tone: ShapeTone, cx: number, cy: number, rx: number, ry: number): DiagramShape {
  return { id, kind: 'ellipse', tone, cx, cy, rx, ry };
}

function _spokes(cx: number, cy: number, r: number): DiagramShape[] {
  const a = r * 0.55;
  return [
    _path('', 'hub', `M ${cx} ${cy - a} L ${cx} ${cy + a} M ${cx - a * 0.85} ${cy - a * 0.35} L ${cx + a * 0.85} ${cy + a * 0.35} M ${cx - a * 0.85} ${cy + a * 0.35} L ${cx + a * 0.85} ${cy - a * 0.35}`),
    _circle('', 'hub', cx, cy, r * 0.13)
  ];
}

function _sideWheels(fx: number, rx: number, cy: number, r: number): DiagramShape[] {
  const one = (id: string, x: number): DiagramShape[] => [
    _circle(id, 'tire', x, cy, r),
    _circle('', 'rim', x, cy, r * 0.58),
    ..._spokes(x, cy, r * 0.48)
  ];
  return [...one('wheel_fl', fx), ...one('wheel_rl', rx)];
}

function _side(type: VehicleBodyType): DiagramShape[] {
  if (type === 'pickup') return _pickupSide();
  if (type === 'hatchback') return _hatchSide();
  if (type === 'suv') return _suvSide();
  if (type === 'van') return _vanSide();
  return _sedanSide();
}

function _ground(): DiagramShape {
  return _path('', 'shadow', 'M 20 378 H 1020');
}

function _sedanSide(): DiagramShape[] {
  return [
    _ground(),
    ..._sideWheels(230, 800, 332, 46),
    _path('', 'paint', 'M 72 248 C 44 228, 42 192, 84 172 L 160 158 L 250 144 L 340 130 L 392 92 L 440 54 C 468 36, 514 26, 568 24 L 705 22 C 748 24, 782 40, 804 70 L 846 116 L 908 130 L 952 140 C 984 148, 1002 174, 994 208 L 980 256 L 962 300 L 910 330 L 865 330 Q 800 198, 735 330 L 295 330 Q 230 198, 165 330 L 108 330 C 86 326, 64 286, 72 248 Z'),
    _path('glass_front', 'glass', 'M 378 116 L 434 62 C 456 46, 496 36, 532 34 L 520 126 L 392 140 Z'),
    _path('', 'glass', 'M 552 36 L 648 34 L 656 122 L 544 128 Z'),
    _path('', 'glass', 'M 678 36 L 758 48 L 746 120 L 670 122 Z'),
    _path('glass_rear', 'glass', 'M 770 56 L 822 98 L 804 116 L 762 108 Z'),
    _path('', 'seam', 'M 538 34 L 522 142'),
    _path('', 'seam', 'M 522 142 L 500 318'),
    _path('', 'seam', 'M 664 34 L 678 318'),
    _path('', 'seam', 'M 155 170 C 340 146, 560 142, 800 162 C 880 174, 940 188, 976 202'),
    _path('', 'seam', 'M 846 116 L 960 142'),
    _path('', 'seam', 'M 285 316 Q 230 228, 175 316'),
    _path('', 'seam', 'M 745 316 Q 800 228, 855 316'),
    _path('headlight_left', 'light', 'M 108 180 C 148 166, 176 180, 168 204 C 146 216, 108 206, 100 190 Z'),
    _path('', 'seam', 'M 120 192 C 146 186, 160 196, 154 204'),
    _path('taillight_left', 'tail', 'M 868 122 L 956 142 L 944 184 L 858 164 Z'),
    _path('mirror_left', 'chrome', 'M 498 104 C 528 92, 550 108, 534 126 C 514 136, 486 120, 498 104 Z'),
    _path('', 'chrome', 'M 575 158 h 24 v 7 h -24 z'),
    _path('', 'chrome', 'M 730 154 h 24 v 7 h -24 z'),
    _path('bumper_front', 'hit', 'M 60 200 C 48 245, 70 320, 130 328 L 155 230 L 140 185 Z'),
    _path('hood', 'hit', 'M 155 172 L 330 138 L 385 100 L 300 170 L 165 198 Z'),
    _path('fender_fl', 'hit', 'M 150 190 L 310 155 L 340 230 Q 230 210, 175 255 L 145 220 Z'),
    _path('door_fl', 'hit', 'M 390 150 L 530 128 L 545 210 L 500 318 L 385 318 Z'),
    _path('door_rl', 'hit', 'M 548 126 L 760 112 L 790 210 L 700 318 L 515 318 Z'),
    _path('roof', 'hit', 'M 455 50 L 568 28 L 700 26 L 675 68 L 470 78 Z'),
    _path('trunk', 'hit', 'M 800 78 L 940 140 L 960 230 L 830 190 Z'),
    _path('fender_rl', 'hit', 'M 760 170 L 900 145 L 940 230 Q 800 210, 755 270 Z'),
    _path('bumper_rear', 'hit', 'M 910 200 L 995 230 L 975 320 L 890 300 Z')
  ];
}

function _hatchSide(): DiagramShape[] {
  return [
    _ground(),
    ..._sideWheels(210, 700, 334, 44),
    _path('', 'paint', 'M 68 254 C 40 234, 38 198, 80 178 L 145 164 L 220 148 L 290 118 L 342 70 C 368 48, 414 34, 468 32 L 590 30 C 644 32, 684 52, 706 88 L 748 148 L 802 178 C 830 204, 840 240, 824 278 L 800 318 L 770 332 L 764 332 Q 700 204, 636 332 L 274 332 Q 210 204, 146 332 L 100 332 C 78 326, 56 288, 68 254 Z'),
    _path('glass_front', 'glass', 'M 276 126 L 346 66 L 450 42 L 432 130 L 288 144 Z'),
    _path('glass_rear', 'glass', 'M 472 40 L 598 36 C 648 48, 688 80, 706 122 L 620 134 L 480 130 Z'),
    _path('', 'seam', 'M 460 40 L 446 150'),
    _path('', 'seam', 'M 446 150 L 424 320'),
    _path('', 'seam', 'M 560 132 L 578 320'),
    _path('', 'seam', 'M 140 174 C 300 150, 500 146, 690 170'),
    _path('', 'seam', 'M 264 318 Q 210 232, 158 318'),
    _path('', 'seam', 'M 646 318 Q 700 232, 752 318'),
    _path('headlight_left', 'light', 'M 96 186 C 134 172, 162 186, 154 208 C 134 220, 96 210, 88 194 Z'),
    _path('taillight_left', 'tail', 'M 748 150 L 812 176 L 798 228 L 736 204 Z'),
    _path('mirror_left', 'chrome', 'M 410 92 C 438 80, 460 96, 446 114 C 426 124, 398 108, 410 92 Z'),
    _path('', 'chrome', 'M 490 156 h 22 v 7 h -22 z'),
    _path('', 'chrome', 'M 630 150 h 22 v 7 h -22 z'),
    _path('bumper_front', 'hit', 'M 52 210 C 44 255, 68 322, 120 330 L 145 230 L 125 180 Z'),
    _path('hood', 'hit', 'M 140 170 L 280 130 L 330 110 L 220 190 Z'),
    _path('fender_fl', 'hit', 'M 130 190 L 260 155 L 290 240 Q 210 215, 155 260 Z'),
    _path('door_fl', 'hit', 'M 300 145 L 450 120 L 455 220 L 424 320 L 300 320 Z'),
    _path('door_rl', 'hit', 'M 460 118 L 640 108 L 680 220 L 600 320 L 440 320 Z'),
    _path('roof', 'hit', 'M 360 60 L 468 34 L 590 32 L 540 90 L 370 100 Z'),
    _path('trunk', 'hit', 'M 680 100 L 800 160 L 820 280 L 700 250 Z'),
    _path('fender_rl', 'hit', 'M 660 180 L 780 160 L 800 250 Q 700 220, 650 270 Z'),
    _path('bumper_rear', 'hit', 'M 790 220 L 845 250 L 825 325 L 760 300 Z')
  ];
}

function _suvSide(): DiagramShape[] {
  return [
    _ground(),
    ..._sideWheels(250, 800, 340, 50),
    _path('', 'paint', 'M 86 228 C 52 206, 50 164, 98 144 L 175 128 L 270 114 L 338 72 L 392 40 C 422 22, 470 12, 530 10 L 690 8 C 750 10, 792 28, 814 60 L 856 112 L 918 132 L 972 148 C 1004 162, 1016 196, 1000 236 L 980 286 L 956 318 L 900 322 L 868 322 Q 800 186, 732 322 L 318 322 Q 250 186, 182 322 L 120 322 C 96 316, 72 268, 86 228 Z'),
    _path('', 'seam', 'M 545 8 H 700'),
    _path('', 'seam', 'M 558 8 V 0 H 688 V 8'),
    _path('glass_front', 'glass', 'M 318 92 L 396 42 L 500 20 L 488 116 L 330 132 Z'),
    _path('glass_rear', 'glass', 'M 522 20 L 688 16 C 740 28, 778 58, 798 96 L 714 116 L 522 114 Z'),
    _path('', 'seam', 'M 508 20 L 494 140'),
    _path('', 'seam', 'M 494 140 L 470 308'),
    _path('', 'seam', 'M 640 112 L 662 308'),
    _path('', 'seam', 'M 155 160 C 340 136, 560 132, 860 158'),
    _path('', 'seam', 'M 308 308 Q 250 220, 196 308'),
    _path('', 'seam', 'M 744 308 Q 800 220, 854 308'),
    _path('headlight_left', 'light', 'M 116 166 C 156 150, 188 166, 178 192 C 154 204, 116 194, 108 176 Z'),
    _path('taillight_left', 'tail', 'M 900 128 L 972 150 L 958 204 L 888 182 Z'),
    _path('mirror_left', 'chrome', 'M 468 68 C 500 54, 524 72, 506 92 C 484 102, 454 84, 468 68 Z'),
    _path('', 'chrome', 'M 530 164 h 26 v 8 h -26 z'),
    _path('', 'chrome', 'M 710 156 h 26 v 8 h -26 z'),
    _circle('', 'seam', 910, 240, 11),
    _path('bumper_front', 'hit', 'M 58 190 C 48 240, 78 312, 140 320 L 165 220 L 145 160 Z'),
    _path('hood', 'hit', 'M 160 150 L 310 120 L 360 80 L 250 175 Z'),
    _path('fender_fl', 'hit', 'M 145 175 L 300 140 L 330 230 Q 250 210, 190 265 Z'),
    _path('door_fl', 'hit', 'M 340 135 L 500 110 L 490 220 L 470 308 L 340 308 Z'),
    _path('door_rl', 'hit', 'M 510 108 L 720 96 L 760 220 L 680 308 L 490 308 Z'),
    _path('roof', 'hit', 'M 410 36 L 530 14 L 690 12 L 650 70 L 420 82 Z'),
    _path('trunk', 'hit', 'M 760 70 L 930 130 L 970 250 L 800 220 Z'),
    _path('fender_rl', 'hit', 'M 750 170 L 900 145 L 940 240 Q 800 215, 745 275 Z'),
    _path('bumper_rear', 'hit', 'M 930 190 L 1010 230 L 990 315 L 900 290 Z')
  ];
}

function _pickupSide(): DiagramShape[] {
  return [
    _ground(),
    ..._sideWheels(210, 860, 336, 48),
    _path('', 'paint', 'M 66 246 C 36 226, 34 188, 76 168 L 148 154 L 230 140 L 300 92 L 352 52 C 378 36, 424 26, 474 24 L 548 22 L 566 108 L 588 122 L 990 128 L 1008 156 L 1012 236 L 996 298 L 968 332 L 928 332 Q 860 196, 792 332 L 278 332 Q 210 196, 142 332 L 96 332 C 74 326, 52 280, 66 246 Z'),
    _path('trunk', 'trim', 'M 584 126 L 984 132 L 976 206 L 576 196 Z'),
    _path('', 'seam', 'M 584 164 H 976'),
    _path('glass_front', 'glass', 'M 286 108 L 356 50 L 486 30 L 496 118 L 298 132 Z'),
    _path('', 'glass', 'M 512 32 L 546 32 L 554 108 L 506 112 Z'),
    _path('', 'seam', 'M 496 30 L 480 148'),
    _path('', 'seam', 'M 480 148 L 456 318'),
    _path('', 'seam', 'M 130 170 C 280 148, 430 144, 540 154'),
    _path('', 'seam', 'M 268 318 Q 210 228, 156 318'),
    _path('', 'seam', 'M 802 318 Q 860 228, 916 318'),
    _path('headlight_left', 'light', 'M 94 180 C 132 166, 162 180, 154 204 C 132 216, 94 206, 86 190 Z'),
    _path('taillight_left', 'tail', 'M 952 140 L 1002 154 L 994 214 L 944 200 Z'),
    _path('mirror_left', 'chrome', 'M 444 74 C 472 60, 494 76, 480 94 C 458 104, 430 88, 444 74 Z'),
    _path('', 'chrome', 'M 420 156 h 24 v 8 h -24 z'),
    _path('bumper_front', 'hit', 'M 48 200 C 40 250, 64 322, 120 330 L 145 230 L 120 175 Z'),
    _path('hood', 'hit', 'M 145 165 L 290 125 L 340 90 L 230 185 Z'),
    _path('fender_fl', 'hit', 'M 135 185 L 270 150 L 300 240 Q 210 215, 160 265 Z'),
    _path('door_fl', 'hit', 'M 310 130 L 490 100 L 480 220 L 456 318 L 300 318 Z'),
    _path('roof', 'hit', 'M 370 48 L 474 26 L 548 24 L 555 95 L 380 110 Z'),
    _path('fender_rl', 'hit', 'M 800 175 L 940 155 L 970 250 Q 860 220, 800 280 Z'),
    _path('bumper_rear', 'hit', 'M 960 175 L 1012 200 L 1000 310 L 950 290 Z')
  ];
}

function _vanSide(): DiagramShape[] {
  return [
    _ground(),
    ..._sideWheels(255, 830, 336, 46),
    _path('', 'paint', 'M 78 214 L 78 92 C 78 52, 112 30, 156 30 L 920 30 C 974 30, 1000 58, 1000 104 L 1000 246 L 984 300 L 948 332 L 898 332 Q 830 204, 762 332 L 324 332 Q 255 204, 186 332 L 110 332 L 78 300 Z'),
    _path('bumper_front', 'trim', 'M 58 228 L 78 214 L 130 250 L 120 312 L 70 312 L 58 260 Z'),
    _path('bumper_rear', 'trim', 'M 948 250 L 1000 246 L 1012 280 L 1000 322 L 948 322 Z'),
    _path('glass_front', 'glass', 'M 86 170 L 86 100 L 220 74 L 230 186 L 94 198 Z'),
    _path('', 'glass', 'M 268 48 H 400 V 128 H 268 Z'),
    _path('', 'glass', 'M 440 48 H 570 V 104 H 440 Z'),
    _path('', 'glass', 'M 600 48 H 730 V 104 H 600 Z'),
    _path('', 'glass', 'M 760 48 H 890 V 104 H 760 Z'),
    _path('', 'seam', 'M 232 42 L 232 248'),
    _path('', 'seam', 'M 410 42 V 248'),
    _path('', 'seam', 'M 314 318 Q 255 236, 200 318'),
    _path('', 'seam', 'M 774 318 Q 830 236, 884 318'),
    _path('', 'chrome', 'M 310 186 h 18 v 38 h -18 z'),
    _path('headlight_left', 'light', 'M 82 176 H 124 V 210 H 82 Z'),
    _path('taillight_left', 'tail', 'M 962 148 H 994 V 240 H 962 Z'),
    _path('mirror_left', 'chrome', 'M 214 116 H 266 V 148 H 214 Z'),
    _path('hood', 'hit', 'M 80 100 L 200 72 L 210 160 L 86 175 Z'),
    _path('door_fl', 'hit', 'M 236 48 L 405 48 L 405 280 L 236 280 Z'),
    _path('door_rl', 'hit', 'M 415 46 L 900 46 L 900 270 L 415 270 Z'),
    _path('roof', 'hit', 'M 160 32 H 910 V 50 H 160 Z'),
    _path('trunk', 'hit', 'M 930 50 L 998 100 L 998 240 L 930 250 Z'),
    _path('fender_fl', 'hit', 'M 160 200 L 300 175 L 330 250 Q 255 230, 180 275 Z'),
    _path('fender_rl', 'hit', 'M 760 190 L 900 175 L 920 255 Q 830 230, 760 280 Z')
  ];
}

function _faceWheels(rear: boolean): DiagramShape[] {
  const left = rear ? 'wheel_rl' : 'wheel_fl';
  const right = rear ? 'wheel_rr' : 'wheel_fr';
  const one = (id: string, x: number): DiagramShape[] => [
    _ellipse(id, 'tire', x, 438, 34, 46),
    _ellipse('', 'rim', x, 438, 13, 20)
  ];
  return [...one(left, 132), ...one(right, 508)];
}

function _face(body: string, rear: boolean, details: DiagramShape[]): DiagramShape[] {
  return [
    _path('', 'shadow', 'M 24 500 H 616'),
    ..._faceWheels(rear),
    _path('', 'paint', body),
    ...details
  ];
}

const SEDAN_FACE = 'M 214 40 H 426 C 458 40, 476 62, 468 88 L 498 154 L 544 196 L 572 248 L 584 330 L 560 452 Q 508 292, 430 452 L 430 478 H 210 L 210 452 Q 132 292, 80 452 L 56 330 L 68 248 L 96 196 L 142 154 L 172 88 C 164 62, 182 40, 214 40 Z';
const SUV_FACE = 'M 200 18 H 440 C 478 18, 500 42, 490 72 L 522 140 L 566 184 L 592 240 L 604 330 L 576 456 Q 508 296, 424 456 L 424 486 H 216 L 216 456 Q 132 296, 64 456 L 36 330 L 48 240 L 74 184 L 118 140 L 150 72 C 140 42, 162 18, 200 18 Z';
const VAN_FACE = 'M 176 16 H 464 L 490 70 L 530 140 L 556 210 L 570 320 L 552 456 Q 508 300, 430 456 L 430 490 H 210 L 210 456 Q 132 300, 88 456 L 70 320 L 84 210 L 110 140 L 150 70 Z';
const TRUCK_FACE = 'M 188 24 H 452 C 486 24, 504 46, 496 74 L 530 140 L 572 180 L 596 236 L 608 340 L 580 458 Q 508 298, 422 458 L 422 488 H 218 L 218 458 Q 132 298, 60 458 L 32 340 L 44 236 L 68 180 L 110 140 L 144 74 C 136 46, 154 24, 188 24 Z';
const HATCH_REAR = 'M 220 52 H 420 C 456 52, 474 80, 466 112 L 512 190 L 552 236 L 572 320 L 556 456 Q 508 300, 432 456 L 432 482 H 208 L 208 456 Q 132 300, 84 456 L 68 320 L 88 236 L 128 190 L 174 112 C 166 80, 184 52, 220 52 Z';
const PICKUP_REAR = 'M 230 78 H 410 V 168 H 530 L 556 240 L 568 340 L 548 458 Q 508 304, 424 458 L 424 492 H 216 L 216 458 Q 132 304, 92 458 L 72 340 L 84 240 L 110 168 H 230 Z';
const VAN_REAR = 'M 168 20 H 472 V 360 L 552 456 Q 508 304, 430 456 L 430 494 H 210 L 210 456 Q 132 304, 88 456 L 168 360 Z';

function _front(type: VehicleBodyType): DiagramShape[] {
  if (type === 'van') return _vanFront();
  if (type === 'pickup') return _pickupFront();
  if (type === 'suv') return _suvFront();
  if (type === 'hatchback') return _hatchFront();
  return _sedanFront();
}

function _rear(type: VehicleBodyType): DiagramShape[] {
  if (type === 'van') return _vanRear();
  if (type === 'pickup') return _pickupRear();
  if (type === 'suv') return _suvRear();
  if (type === 'hatchback') return _hatchRear();
  return _sedanRear();
}

function _sedanFront(): DiagramShape[] {
  return _face(SEDAN_FACE, false, [
    _path('glass_front', 'glass', 'M 184 92 L 150 164 H 490 L 456 92 Z'),
    _path('', 'seam', 'M 248 58 H 392'),
    _path('headlight_left', 'light', 'M 148 188 C 210 174, 258 190, 262 224 C 254 254, 202 266, 150 250 C 128 238, 126 204, 148 188 Z'),
    _path('', 'seam', 'M 166 206 C 208 198, 236 212, 232 232'),
    _path('headlight_right', 'light', 'M 492 188 C 430 174, 382 190, 378 224 C 386 254, 438 266, 490 250 C 512 238, 514 204, 492 188 Z'),
    _path('', 'seam', 'M 474 206 C 432 198, 404 212, 408 232'),
    _path('', 'grille', 'M 252 196 H 388 V 260 H 252 Z'),
    _path('', 'seam', 'M 264 210 H 376 M 264 224 H 376 M 264 238 H 376 M 264 252 H 376'),
    _path('bumper_front', 'trim', 'M 200 300 H 440 V 360 H 200 Z'),
    _path('', 'grille', 'M 230 314 H 410 V 344 H 230 Z'),
    _circle('', 'light', 188, 390, 13),
    _circle('', 'light', 452, 390, 13),
    _path('mirror_left', 'chrome', 'M 58 124 H 108 L 98 172 H 66 Z'),
    _path('mirror_right', 'chrome', 'M 582 124 H 532 L 542 172 H 574 Z'),
    _path('roof', 'hit', 'M 220 44 H 420 L 400 86 H 240 Z'),
    _path('hood', 'hit', 'M 145 170 H 495 L 470 295 H 170 Z'),
    _path('fender_fl', 'hit', 'M 70 210 L 145 175 L 165 320 L 85 340 Z'),
    _path('fender_fr', 'hit', 'M 570 210 L 495 175 L 475 320 L 555 340 Z')
  ]);
}

function _sedanRear(): DiagramShape[] {
  return _face(SEDAN_FACE, true, [
    _path('glass_rear', 'glass', 'M 196 94 L 168 158 H 472 L 444 94 Z'),
    _path('', 'seam', 'M 180 184 H 460'),
    _path('taillight_left', 'tail', 'M 146 198 H 236 C 252 198, 256 258, 234 260 H 146 Z'),
    _path('taillight_right', 'tail', 'M 494 198 H 404 C 388 198, 384 258, 406 260 H 494 Z'),
    _path('', 'chrome', 'M 270 210 H 370 V 252 H 270 Z'),
    _path('', 'seam', 'M 190 276 H 450'),
    _path('bumper_rear', 'trim', 'M 200 304 H 440 V 368 H 200 Z'),
    _path('roof', 'hit', 'M 220 44 H 420 L 400 86 H 240 Z'),
    _path('trunk', 'hit', 'M 160 165 H 480 L 460 300 H 180 Z'),
    _path('fender_rl', 'hit', 'M 70 210 L 145 175 L 165 320 L 85 340 Z'),
    _path('fender_rr', 'hit', 'M 570 210 L 495 175 L 475 320 L 555 340 Z')
  ]);
}

function _hatchFront(): DiagramShape[] {
  return _face(SEDAN_FACE, false, [
    _path('glass_front', 'glass', 'M 190 96 L 156 172 H 484 L 450 96 Z'),
    _path('headlight_left', 'light', 'M 154 196 C 214 182, 258 198, 262 230 C 254 258, 206 270, 156 254 C 136 242, 134 210, 154 196 Z'),
    _path('headlight_right', 'light', 'M 486 196 C 426 182, 382 198, 378 230 C 386 258, 434 270, 484 254 C 504 242, 506 210, 486 196 Z'),
    _path('', 'grille', 'M 268 204 H 372 V 262 H 268 Z'),
    _path('', 'seam', 'M 280 218 H 360 M 280 232 H 360 M 280 246 H 360'),
    _path('bumper_front', 'trim', 'M 206 304 H 434 V 366 H 206 Z'),
    _circle('', 'light', 190, 392, 12),
    _circle('', 'light', 450, 392, 12),
    _path('mirror_left', 'chrome', 'M 60 130 H 108 L 98 176 H 68 Z'),
    _path('mirror_right', 'chrome', 'M 580 130 H 532 L 542 176 H 572 Z'),
    _path('roof', 'hit', 'M 220 44 H 420 L 400 90 H 240 Z'),
    _path('hood', 'hit', 'M 150 178 H 490 L 465 298 H 175 Z'),
    _path('fender_fl', 'hit', 'M 70 215 L 150 180 L 168 325 L 88 345 Z'),
    _path('fender_fr', 'hit', 'M 570 215 L 490 180 L 472 325 L 552 345 Z')
  ]);
}

function _hatchRear(): DiagramShape[] {
  return _face(HATCH_REAR, true, [
    _path('glass_rear', 'glass', 'M 196 78 L 158 176 H 482 L 444 78 Z'),
    _path('taillight_left', 'tail', 'M 130 196 H 228 V 258 H 130 Z'),
    _path('taillight_right', 'tail', 'M 510 196 H 412 V 258 H 510 Z'),
    _path('', 'chrome', 'M 272 214 H 368 V 252 H 272 Z'),
    _path('bumper_rear', 'trim', 'M 160 300 H 480 V 372 H 160 Z'),
    _path('roof', 'hit', 'M 230 56 H 410 L 395 100 H 245 Z'),
    _path('trunk', 'hit', 'M 165 180 H 475 L 455 295 H 185 Z'),
    _path('fender_rl', 'hit', 'M 75 220 L 140 185 L 160 330 L 90 350 Z'),
    _path('fender_rr', 'hit', 'M 565 220 L 500 185 L 480 330 L 550 350 Z')
  ]);
}

function _suvFront(): DiagramShape[] {
  return _face(SUV_FACE, false, [
    _path('', 'seam', 'M 248 16 H 392'),
    _path('', 'seam', 'M 270 8 H 370'),
    _path('glass_front', 'glass', 'M 164 78 L 124 170 H 516 L 476 78 Z'),
    _path('headlight_left', 'light', 'M 136 190 C 206 174, 264 194, 268 232 C 258 264, 196 276, 138 258 C 112 244, 112 206, 136 190 Z'),
    _path('headlight_right', 'light', 'M 504 190 C 434 174, 376 194, 372 232 C 382 264, 444 276, 502 258 C 528 244, 528 206, 504 190 Z'),
    _path('', 'grille', 'M 232 198 H 408 V 278 H 232 Z'),
    _path('', 'seam', 'M 244 212 H 396 M 244 228 H 396 M 244 244 H 396 M 244 260 H 396'),
    _path('bumper_front', 'trim', 'M 168 312 H 472 V 378 H 168 Z'),
    _circle('', 'light', 176, 400, 14),
    _circle('', 'light', 464, 400, 14),
    _path('mirror_left', 'chrome', 'M 40 112 H 104 L 92 170 H 50 Z'),
    _path('mirror_right', 'chrome', 'M 600 112 H 536 L 548 170 H 590 Z'),
    _path('roof', 'hit', 'M 210 22 H 430 L 410 70 H 230 Z'),
    _path('hood', 'hit', 'M 140 176 H 500 L 475 305 H 165 Z'),
    _path('fender_fl', 'hit', 'M 55 200 L 140 165 L 165 320 L 70 345 Z'),
    _path('fender_fr', 'hit', 'M 585 200 L 500 165 L 475 320 L 570 345 Z')
  ]);
}

function _suvRear(): DiagramShape[] {
  return _face(SUV_FACE, true, [
    _path('', 'seam', 'M 270 8 H 370'),
    _path('glass_rear', 'glass', 'M 172 80 L 136 168 H 504 L 468 80 Z'),
    _path('taillight_left', 'tail', 'M 128 196 H 246 V 264 H 128 Z'),
    _path('taillight_right', 'tail', 'M 512 196 H 394 V 264 H 512 Z'),
    _path('', 'chrome', 'M 268 214 H 372 V 258 H 268 Z'),
    _path('bumper_rear', 'trim', 'M 168 320 H 472 V 392 H 168 Z'),
    _path('roof', 'hit', 'M 210 22 H 430 L 410 72 H 230 Z'),
    _path('trunk', 'hit', 'M 150 175 H 490 L 470 315 H 170 Z'),
    _path('fender_rl', 'hit', 'M 55 205 L 140 170 L 165 325 L 70 348 Z'),
    _path('fender_rr', 'hit', 'M 585 205 L 500 170 L 475 325 L 570 348 Z')
  ]);
}

function _pickupFront(): DiagramShape[] {
  return _face(TRUCK_FACE, false, [
    _path('glass_front', 'glass', 'M 160 80 L 124 162 H 516 L 480 80 Z'),
    _path('headlight_left', 'light', 'M 112 186 H 246 V 242 H 124 Z'),
    _path('headlight_right', 'light', 'M 528 186 H 394 V 242 H 516 Z'),
    _path('', 'grille', 'M 228 178 H 412 V 260 H 228 Z'),
    _path('', 'seam', 'M 240 192 H 400 M 240 206 H 400 M 240 220 H 400 M 240 234 H 400 M 240 248 H 400'),
    _path('bumper_front', 'trim', 'M 120 292 H 520 V 360 H 120 Z'),
    _path('mirror_left', 'chrome', 'M 28 116 H 96 L 86 170 H 38 Z'),
    _path('mirror_right', 'chrome', 'M 612 116 H 544 L 554 170 H 602 Z'),
    _path('roof', 'hit', 'M 200 28 H 440 L 420 72 H 220 Z'),
    _path('hood', 'hit', 'M 130 168 H 510 L 490 285 H 150 Z'),
    _path('fender_fl', 'hit', 'M 45 200 L 120 165 L 145 330 L 55 350 Z'),
    _path('fender_fr', 'hit', 'M 595 200 L 520 165 L 495 330 L 585 350 Z')
  ]);
}

function _pickupRear(): DiagramShape[] {
  return _face(PICKUP_REAR, true, [
    _path('glass_rear', 'glass', 'M 248 92 H 392 V 150 H 248 Z'),
    _path('taillight_left', 'tail', 'M 118 200 H 196 V 292 H 118 Z'),
    _path('taillight_right', 'tail', 'M 522 200 H 444 V 292 H 522 Z'),
    _path('trunk', 'trim', 'M 200 220 H 440 V 390 H 200 Z'),
    _path('', 'chrome', 'M 250 300 H 390 V 340 H 250 Z'),
    _path('bumper_rear', 'trim', 'M 230 418 H 410 V 458 H 230 Z'),
    _path('roof', 'hit', 'M 235 80 H 405 V 160 H 235 Z'),
    _path('fender_rl', 'hit', 'M 90 200 L 160 175 L 185 330 L 100 350 Z'),
    _path('fender_rr', 'hit', 'M 550 200 L 480 175 L 455 330 L 540 350 Z')
  ]);
}

function _vanFront(): DiagramShape[] {
  return _face(VAN_FACE, false, [
    _path('glass_front', 'glass', 'M 150 78 L 118 228 H 522 L 490 78 Z'),
    _path('headlight_left', 'light', 'M 96 248 H 168 V 300 H 96 Z'),
    _path('headlight_right', 'light', 'M 544 248 H 472 V 300 H 544 Z'),
    _path('bumper_front', 'trim', 'M 120 328 H 520 V 400 H 120 Z'),
    _path('', 'grille', 'M 240 346 H 400 V 378 H 240 Z'),
    _path('mirror_left', 'chrome', 'M 28 150 H 96 L 86 230 H 40 Z'),
    _path('mirror_right', 'chrome', 'M 612 150 H 544 L 554 230 H 600 Z'),
    _path('roof', 'hit', 'M 190 20 H 450 V 70 H 190 Z'),
    _path('hood', 'hit', 'M 150 230 H 490 V 320 H 150 Z'),
    _path('fender_fl', 'hit', 'M 80 220 L 145 180 L 170 340 L 95 360 Z'),
    _path('fender_fr', 'hit', 'M 560 220 L 495 180 L 470 340 L 545 360 Z')
  ]);
}

function _vanRear(): DiagramShape[] {
  return _face(VAN_REAR, true, [
    _path('', 'seam', 'M 320 36 V 300'),
    _path('', 'chrome', 'M 248 170 H 300 V 220 H 248 Z'),
    _path('', 'chrome', 'M 340 170 H 392 V 220 H 340 Z'),
    _path('taillight_left', 'tail', 'M 176 70 H 210 V 280 H 176 Z'),
    _path('taillight_right', 'tail', 'M 464 70 H 430 V 280 H 464 Z'),
    _path('bumper_rear', 'trim', 'M 168 330 H 472 V 410 H 168 Z'),
    _path('mirror_left', 'chrome', 'M 100 120 H 168 V 210 H 100 Z'),
    _path('mirror_right', 'chrome', 'M 472 120 H 540 V 210 H 472 Z'),
    _path('roof', 'hit', 'M 175 24 H 465 V 60 H 175 Z'),
    _path('trunk', 'hit', 'M 215 70 H 425 V 320 H 215 Z'),
    _path('fender_rl', 'hit', 'M 90 250 L 165 220 L 190 350 L 100 370 Z'),
    _path('fender_rr', 'hit', 'M 550 250 L 475 220 L 450 350 L 540 370 Z')
  ]);
}

const CAR_TOP = 'M 96 150 C 70 132, 86 108, 140 100 L 280 88 L 400 64 L 760 64 L 860 92 L 970 116 L 996 170 L 970 224 L 860 248 L 760 276 L 400 276 L 280 252 L 140 240 C 86 232, 70 208, 96 150 Z';
const PICKUP_TOP = 'M 90 148 C 66 130, 82 108, 136 100 L 260 90 L 360 70 L 520 70 L 540 100 L 980 108 L 996 170 L 980 232 L 540 240 L 520 270 L 360 270 L 260 250 L 136 240 C 82 232, 66 210, 90 148 Z';

function _topWheels(): DiagramShape[] {
  const one = (id: string, x: number, y: number): DiagramShape[] => [
    _ellipse(id, 'tire', x, y, 58, 18),
    _ellipse('', 'rim', x, y, 28, 8)
  ];
  return [
    ...one('wheel_fl', 250, 62),
    ...one('wheel_rl', 790, 62),
    ...one('wheel_fr', 250, 278),
    ...one('wheel_rr', 790, 278)
  ];
}

function _top(type: VehicleBodyType): DiagramShape[] {
  if (type === 'pickup') return _pickupTop();
  if (type === 'van') return _vanTop();
  return [
    ..._topWheels(),
    _path('', 'paint', CAR_TOP),
    _path('glass_front', 'glass', 'M 400 78 L 500 100 V 240 L 400 262 Z'),
    _path('', 'seam', 'M 500 170 H 740'),
    _path('glass_rear', 'glass', 'M 720 100 H 780 V 240 H 720 Z'),
    _path('mirror_left', 'chrome', 'M 360 70 H 420 V 96 H 360 Z'),
    _path('mirror_right', 'chrome', 'M 360 244 H 420 V 270 H 360 Z'),
    _path('bumper_front', 'hit', 'M 90 125 L 160 100 L 160 240 L 90 215 Z'),
    _path('hood', 'hit', 'M 165 100 L 395 72 L 395 268 L 165 240 Z'),
    _path('roof', 'hit', 'M 505 100 H 715 V 240 H 505 Z'),
    _path('door_fl', 'hit', 'M 420 64 H 740 V 98 H 420 Z'),
    _path('door_fr', 'hit', 'M 420 242 H 740 V 276 H 420 Z'),
    ...(type === 'hatchback' || type === 'suv' ? [] : [
      _path('door_rl', 'hit', 'M 620 64 H 760 V 98 H 620 Z'),
      _path('door_rr', 'hit', 'M 620 242 H 760 V 276 H 620 Z')
    ]),
    _path('trunk', 'hit', 'M 800 110 H 960 V 230 H 800 Z'),
    _path('bumper_rear', 'hit', 'M 950 125 L 990 145 L 990 195 L 950 215 Z'),
    _path('fender_fl', 'hit', 'M 200 88 H 310 V 120 H 200 Z'),
    _path('fender_fr', 'hit', 'M 200 220 H 310 V 252 H 200 Z'),
    _path('fender_rl', 'hit', 'M 740 88 H 860 V 120 H 740 Z'),
    _path('fender_rr', 'hit', 'M 740 220 H 860 V 252 H 740 Z')
  ];
}

function _pickupTop(): DiagramShape[] {
  return [
    ..._topWheels(),
    _path('', 'paint', PICKUP_TOP),
    _path('glass_front', 'glass', 'M 360 84 L 390 104 H 520 V 236 H 390 L 360 256 Z'),
    _path('trunk', 'trim', 'M 560 112 H 960 V 228 H 560 Z'),
    _path('', 'seam', 'M 560 170 H 960'),
    _path('mirror_left', 'chrome', 'M 330 64 H 390 V 92 H 330 Z'),
    _path('mirror_right', 'chrome', 'M 330 248 H 390 V 276 H 330 Z'),
    _path('bumper_front', 'hit', 'M 85 125 L 150 105 L 150 235 L 85 215 Z'),
    _path('hood', 'hit', 'M 155 100 L 350 78 L 350 262 L 155 240 Z'),
    _path('roof', 'hit', 'M 395 100 H 515 V 240 H 395 Z'),
    _path('door_fl', 'hit', 'M 370 68 H 520 V 100 H 370 Z'),
    _path('door_fr', 'hit', 'M 370 240 H 520 V 272 H 370 Z'),
    _path('bumper_rear', 'hit', 'M 960 130 L 998 150 L 998 190 L 960 210 Z'),
    _path('fender_fl', 'hit', 'M 190 90 H 310 V 118 H 190 Z'),
    _path('fender_fr', 'hit', 'M 190 222 H 310 V 250 H 190 Z'),
    _path('fender_rl', 'hit', 'M 740 100 H 880 V 125 H 740 Z'),
    _path('fender_rr', 'hit', 'M 740 215 H 880 V 240 H 740 Z')
  ];
}

function _vanTop(): DiagramShape[] {
  return [
    ..._topWheels(),
    _path('', 'paint', 'M 120 108 H 960 V 232 H 120 Z'),
    _path('glass_front', 'glass', 'M 140 120 L 200 96 H 300 V 244 H 200 L 140 220 Z'),
    _path('', 'seam', 'M 320 108 V 232'),
    _path('mirror_left', 'chrome', 'M 280 48 H 340 V 108 H 280 Z'),
    _path('mirror_right', 'chrome', 'M 280 232 H 340 V 292 H 280 Z'),
    _path('hood', 'hit', 'M 125 115 L 195 100 L 195 240 L 125 225 Z'),
    _path('roof', 'hit', 'M 330 112 H 900 V 228 H 330 Z'),
    _path('door_fl', 'hit', 'M 200 108 H 320 V 140 H 200 Z'),
    _path('door_fr', 'hit', 'M 200 200 H 320 V 232 H 200 Z'),
    _path('door_rl', 'hit', 'M 330 108 H 900 V 140 H 330 Z'),
    _path('door_rr', 'hit', 'M 330 200 H 900 V 232 H 330 Z'),
    _path('trunk', 'hit', 'M 910 120 H 955 V 220 H 910 Z'),
    _path('bumper_front', 'hit', 'M 120 130 H 145 V 210 H 120 Z'),
    _path('bumper_rear', 'hit', 'M 945 125 H 960 V 215 H 945 Z')
  ];
}
